// AHC PHC Capacity Assessment — Complete Data Structure V4 Revised
// Frontend mirror of backend/utils/assessmentData.js

export const assessmentData = {
  title: "AHC PHC CAPACITY ASSESSMENT",
  version: "V5 Revised",
  organisation: "MULU HEALTH CONSULTANCY",
  maxTotal: 1000,
  ratings: [
    {
      label: "Level A",
      min: 801,
      max: 1000,
      color: "#16a34a",
      bg: "#dcfce7",
      description: "Exemplary",
    },
    {
      label: "Level B",
      min: 601,
      max: 800,
      color: "#2563eb",
      bg: "#dbeafe",
      description: "Established",
    },
    {
      label: "Level C",
      min: 401,
      max: 600,
      color: "#d97706",
      bg: "#fef3c7",
      description: "Developing",
    },
    {
      label: "Level D",
      min: 0,
      max: 400,
      color: "#dc2626",
      bg: "#fee2e2",
      description: "Emerging",
    },
  ],
  pillars: [
    {
      id: "p1",
      code: "P1",
      name: "Training & Workforce Development",
      emoji: "📚",
      weight: 0.45,
      maxRaw: 104,
      maxWeighted: 450,
      weightLabel: "45%",
      color: "#1a365d",
      lightColor: "#ebf8ff",
      domains: [
        {
          id: "d1_1",
          code: "1.1",
          name: "Curriculum & Pedagogical Quality",
          maxRaw: 36,
          coreIndicators: ["1.1.1", "1.1.2", "1.1.4"],
          indicators: [
            {
              code: "1.1.1",
              name: "PHC Core Competency Integration",
              isCore: true,
              herqaStd: "Std 3",
              v4note: null,
              levels: {
                1: "No PHC competency framework. PHC topics appear incidentally in the curriculum, with no mapping to national standards.",
                2: "PHC competencies are referenced but not formally mapped or approved. Some programs include PHC content and others do not; coverage is inconsistent and depends on individual programs rather than an institutional framework.",
                3: "A documented PHC competency framework is formally approved and applied across all program levels the institution offers (for example BSc, MSc, MD, PhD, specialty — whichever it runs). PHC competencies are explicitly mapped and aligned to the national PHC strategy and HERQA/ETA Standards, and the framework has been reviewed within the last three years. The framework, its mapping, and the review record can each be evidenced in documents.",
                4: "All of Level 3, and in addition: the framework was co-developed with relevant external stakeholders (for example the Ministry of Health, Regional Health Bureaus, professional councils, HERQA/ETA, and employers of graduates) and is regularly validated with them; and it has demonstrable influence beyond the institution — for example serving as a regional or national reference model, or being adopted or adapted by other institutions.",
              },
            },
            {
              code: "1.1.2",
              name: "PHC Curriculum Review Process",
              isCore: true,
              herqaStd: "Std 3",
              v4note: null,
              levels: {
                1: "No formal curriculum review process. The curriculum has remained unchanged since its original development.",
                2: "Reviews happen only ad hoc, when issues are raised informally. There is no scheduled review cycle and no formal review committee.",
                3: "A formal curriculum review committee reviews the curriculum on a regular, scheduled cycle — at least once every three years, but not yet on a continuous or annual basis. Reviews evaluate PHC content against current MoH guidelines and HERQA/ETA standards, and meeting minutes are documented. The review schedule, the standards used, and the minutes can each be evidenced.",
                4: "All of Level 3, and in addition: the review operates on an annual or continuous cycle; it draws structured external input from the MoH, Regional Health Bureaus, students, and Health Extension Workers (and other relevant stakeholders); and curriculum changes are tracked over time and publicly reported.",
              },
            },
            {
              code: "1.1.3",
              name: "Stakeholder Feedback Integration",
              isCore: false,
              herqaStd: "Std 3",
              v4note: "Expanded V4",
              levels: {
                1: "No mechanism to gather feedback from the MoH, RHBs, or health workers on curriculum relevance.",
                2: "Feedback is collected informally. There is no structured form or schedule, and findings are not documented.",
                3: "A structured annual feedback process is in place: RHBs, health center heads, and HEWs are formally surveyed or invited to advisory meetings, and findings are fed into the next curriculum review cycle. The feedback process and its use in review can be evidenced.",
                4: "All of Level 3, and in addition: a standing Community Advisory Board — including HEWs, community leaders, and religious leaders — meets at least twice a year to co-review curriculum priorities, so that community and health-system stakeholders actively shape the curriculum rather than only commenting on it.",
              },
            },
            {
              code: "1.1.4",
              name: "Competency-Based Teaching & Assessment",
              isCore: true,
              herqaStd: "Std 5",
              v4note: null,
              levels: {
                1: "Teaching is exclusively lecture-based. Assessment tests knowledge only, with no requirement for students to demonstrate competencies.",
                2: "Some simulation or skills demonstrations are included, but they are not standardized. Assessment blueprints are informal and applied inconsistently across programs or courses.",
                3: "Competency-Based Education (CBE) is the documented institutional standard. Assessment blueprints explicitly align learning objectives to PHC competencies, and competency assessments — such as OSCEs and structured clinical skills assessments — are used, with records maintained. The CBE standard, the blueprints, and the assessment records can each be evidenced.",
                4: "All of Level 3, and in addition: competency attainment is tracked per student, including through digital systems; assessment blueprints are externally validated (for example reviewed or endorsed by HERQA/ETA or a relevant professional council); and the institution demonstrates leadership in CBE beyond its own walls — for example training faculty from peer HEIs, or its CBE methodology being adopted or adapted elsewhere.",
              },
            },
            {
              code: "1.1.5",
              name: "Digital Health & Telemedicine Integration",
              isCore: false,
              herqaStd: "Std 3",
              v4note: null,
              levels: {
                1: "No digital health content in any health science program.",
                2: "Digital health is mentioned in lecture notes only. There is no dedicated module, no practical exercises, and no competency assessment.",
                3: "Digital health is taught formally across the institution’s PHC-relevant health science programs, with at least one dedicated module covering digital health tools, mHealth, or telemedicine relevant to Ethiopia’s PHC system, and including practical exercises. The module content and the practical component can be evidenced.",
                4: "All of Level 3, and in addition: digital health is a cross-cutting competency thread running through the curriculum rather than a single module; and students complete a practicum using realistic digital health platforms — including those used in Ethiopia’s PHC system (for example RHB or MoH platforms) where access allows.",
              },
            },
            {
              code: "1.1.6",
              name: "Interprofessional Education (IPE)",
              isCore: false,
              herqaStd: "Std 3",
              v4note: null,
              levels: {
                1: "No IPE activities. Students from different health disciplines never learn together.",
                2: "Occasional joint lectures occur, but there is no structured IPE curriculum and no interprofessional competency framework.",
                3: "IPE is a formal component of the curriculum. Students from at least two disciplines take part in structured, PHC team-based learning, and IPE competencies are assessed. The IPE curriculum component and the competency assessment can be evidenced.",
                4: "All of Level 3, and in addition: IPE extends across the range of health disciplines the institution offers, and students complete joint PHC field placements in interprofessional teams; faculty include trained IPE facilitators; and the IPE program has recognition beyond the institution — for example being referenced, adopted, or formally acknowledged by a national body, professional council, or other institutions.",
              },
            },
          ],
        },
        {
          id: "d1_2",
          code: "1.2",
          name: "Faculty Competence & Professional Development",
          maxRaw: 32,
          coreIndicators: ["1.2.1", "1.2.3", "1.2.5"],
          indicators: [
            {
              code: "1.2.1",
              name: "Faculty PHC Expertise & Student Ratio",
              isCore: true,
              herqaStd: "Std 4",
              v4note: null,
              levels: {
                1: "Faculty-to-student ratio worse than 1:40, and no faculty member holds a postgraduate qualification in a PHC-relevant discipline.",
                2: "Ratio between 1:26 and 1:40. Some faculty hold PHC-relevant qualifications, but this expertise is concentrated in only one or two individuals.",
                3: "Documented ratio between 1:21 and 1:25 (meeting HERQA Standard 4), and at least 60% of teaching faculty hold an MSc or PhD in a PHC-relevant discipline. Both the ratio and the qualification profile can be evidenced from HR records.",
                4: "All of Level 3, and in addition: ratio of 1:20 or better; effectively all PHC-teaching faculty hold postgraduate qualifications (at least 90%, with documented progression for any remainder); faculty publish on PHC issues; and the institution runs a faculty exchange or secondment program with the MoH or Regional Health Bureaus.",
              },
            },
            {
              code: "1.2.2",
              name: "Faculty Advanced Qualifications",
              isCore: false,
              herqaStd: "Std 4",
              v4note: null,
              levels: {
                1: "Fewer than 30% of health sciences faculty hold postgraduate qualifications (MSc or PhD).",
                2: "30–50% hold postgraduate qualifications. There is no institution-wide target or faculty improvement plan.",
                3: "51–70% hold postgraduate qualifications, and a documented faculty development plan sets a target (for example 70% within five years) with evidence of implementation.",
                4: "All of Level 3, and in addition: more than 70% hold postgraduate qualifications; the institution maintains an active, documented pipeline of faculty completing PhDs (proportionate to faculty size); and faculty are engaged in international collaborative research degrees.",
              },
            },
            {
              code: "1.2.3",
              name: "CPD Programme Coverage",
              isCore: true,
              herqaStd: "Std 4",
              v4note: null,
              levels: {
                1: "No structured CPD. Faculty attend external events at their own initiative, with no institutional tracking.",
                2: "A CPD calendar exists but uptake is below 40% of faculty. Budget is insufficient, and PHC-specific CPD events are rare.",
                3: "Structured CPD reaching the majority of faculty (uptake of at least 60%), with a minimum of 20 hours per faculty member per year (or the national CPD requirement, where set). A meaningful share of CPD is PHC-specific, attendance is tracked, and participation is linked to annual performance review. Uptake, hours, and the performance-review link can be evidenced.",
                4: "All of Level 3, and in addition: high faculty coverage approaching the full faculty; 30 or more CPD hours per faculty member per year (or above the national requirement); CPD is institution-funded and includes inter-institutional and international opportunities; and PHC CPD modules are co-designed with the MoH.",
              },
            },
            {
              code: "1.2.4",
              name: "Research Mentorship for Junior Faculty",
              isCore: false,
              herqaStd: "Std 4",
              v4note: null,
              levels: {
                1: "No formal mentorship. Junior faculty and postgraduate students navigate research independently.",
                2: "Some senior faculty informally mentor juniors, but there is no structure, documentation, or evaluation.",
                3: "A formal mentorship program assigns each junior faculty member and PhD student to a designated senior mentor. Terms of reference exist and mentorship meetings are documented. The program’s structure and records can be evidenced.",
                4: "All of Level 3, and in addition: the program demonstrably produces research outcomes for mentees — for example mentored junior faculty and students publishing on PHC issues, completing research degrees, presenting at conferences, or securing grants — and these outcomes are tracked over time. (As longitudinal data accumulate, a strong program would show the majority of mentees achieving such outcomes within a few years.)",
              },
            },
            {
              code: "1.2.5",
              name: "Graduate PHC Employment & Outcome Tracking",
              isCore: true,
              herqaStd: "Std 6",
              v4note: "Outcome focus strengthened V4",
              levels: {
                1: "No tracking of graduate employment. No alumni database and no outcome data of any kind.",
                2: "Graduate employment is tracked only informally or for recent cohorts. The PHC placement rate is unknown, and there is no employer feedback loop.",
                3: "An annual graduate employment survey is conducted. The PHC employment rate is documented and reported to HERQA and the MoH, and employer satisfaction feedback is used in curriculum review. The survey, the reported rate, and the feedback-into-review link can be evidenced.",
                4: "All of Level 3, and in addition: the institution maintains a continuous (near real-time) alumni tracking system; the PHC placement rate is verified with external partners such as RHBs and published, together with employer satisfaction results; and the institution assesses graduate PHC competency post-graduation (for example at 12 and 24 months, as longitudinal data accumulate) and feeds the findings back into training.",
              },
            },
          ],
        },
        {
          id: "d1_3",
          code: "1.3",
          name: "Clinical Practice Sites & Learning Infrastructure",
          maxRaw: 36,
          coreIndicators: ["1.3.1", "1.3.2", "1.3.6"],
          indicators: [
            {
              code: "1.3.1",
              name: "PHC Practice Site Quality & Affiliation",
              isCore: true,
              herqaStd: "Std 7",
              v4note: null,
              levels: {
                1: "Students are placed in hospital wards only. There is no MoU with any community-level facility.",
                2: "Some placements at health centers exist but are informal. MoUs are absent or expired, and there is no university oversight of the sites.",
                3: "Every active PHC practice site is covered by a current, signed MoU (allowing for documented renewals in progress). University supervisors make documented visits, and a site quality checklist is used. The MoUs, visit records, and checklists can be evidenced.",
                4: "All of Level 3, and in addition: practice sites are assessed on a regular cycle (at least annually) against a recognized PHC site quality framework (national where one exists, co-developed with the MoH); and students rotate across the levels of the PHCU system available in the institution’s catchment (health post, health center and primary hospital where accessible).",
              },
            },
            {
              code: "1.3.2",
              name: "Simulation Lab / Skills Centre",
              isCore: true,
              herqaStd: "Std 7",
              v4note: null,
              levels: {
                1: "No dedicated simulation or skills lab. Students practice clinical skills directly on patients.",
                2: "A room is designated as a skills lab, but equipment is inadequate or broken and the lab is used only sporadically.",
                3: "A functional simulation lab is equipped for core PHC skills (for example ANC, delivery, neonatal resuscitation, wound care), with a schedule and utilization records maintained. The equipment and utilization records can be evidenced.",
                4: "All of Level 3, and in addition: a well-equipped simulation center with equipment appropriate to the full range of PHC skills (including higher-fidelity simulation where relevant), structured integration into the curriculum across programs, and a trained skills-lab coordinator or team; and the center’s use extends beyond the institution’s own students — for example shared with RHBs or other institutions for in-service training. (An institution serving as a national simulation training hub represents the top of this level.)",
              },
            },
            {
              code: "1.3.3",
              name: "Clinical Supervision & Mentorship",
              isCore: false,
              herqaStd: "Std 7",
              v4note: null,
              levels: {
                1: "No structured supervision. Students are placed at sites with no formal supervisor assignment.",
                2: "Supervisors are assigned on paper, but visits are infrequent. There is no standard supervision form, and logbooks are maintained inconsistently.",
                3: "Each student has a named university supervisor who makes documented visits at least twice per rotation (proportionate to rotation length). A standard supervision form is used, and student logbooks are reviewed and signed. The supervisor assignments, visit records, and signed logbooks can be evidenced.",
                4: "All of Level 3, and in addition: a tiered supervision model operates — combining peer-to-peer support, a site-based mentor, and the university supervisor — with clear roles at each tier; and supervision records support timely monitoring, including, where available, digital logbooks enabling real-time oversight.",
              },
            },
            {
              code: "1.3.4",
              name: "Library & e-Resource Access",
              isCore: false,
              herqaStd: "Std 7",
              v4note: null,
              levels: {
                1: "Library holdings are outdated (older than 10 years). There are no journal subscriptions, and internet access is unavailable or unreliable.",
                2: "Some recent holdings exist but are insufficient for the number of students enrolled. Journal access is limited, and there is no e-library portal.",
                3: "The library has adequate, current holdings for the PHC disciplines taught, with active access to current PHC-relevant journals and databases (for example through individual subscriptions or HINARI), and an accessible student e-library portal. Holdings, journal/database access, and the portal can be evidenced.",
                4: "All of Level 3, and in addition: broad open or institutional access to major databases (such as HINARI, PubMed, and AJOL); a digital repository of theses and institutional research; and extended, flexible access to resources, including continuous (for example 24/7) digital access through the e-portal. (A library serving as a national or regional resource center represents the top of this level.)",
              },
            },
            {
              code: "1.3.5",
              name: "Student Welfare & Support Services",
              isCore: false,
              herqaStd: "Std 7",
              v4note: null,
              levels: {
                1: "No designated student welfare service. No counselling, health service, or internship support.",
                2: "A student affairs office exists but is under-resourced. Counselling is available on request only, and there is no structured career guidance.",
                3: "An operational student health clinic or a clear referral pathway is in place; a trained counsellor is available on a defined schedule; and career guidance connects students to PHC employment pathways. The health service/referral pathway, the counselling schedule, and the career guidance can be evidenced.",
                4: "All of Level 3, and in addition: a comprehensive student-support service that meets or exceeds HERQA/ETA’s student-support requirements; a mental health service offering both group and individual sessions; and a career office with documented partnerships with RHBs (and other employers) supporting graduate placement into PHC roles.",
              },
            },
            {
              code: "1.3.6",
              name: "Short-term & Executive PHC Training Programs [NEW]",
              isCore: true,
              herqaStd: "Std 7",
              v4note: null,
              levels: {
                1: "No short-term or continuing-education training offered to in-service PHC workers, and no executive or refresher programs for PHC officers.",
                2: "Occasional, ad hoc trainings for PHC workers take place, but they are not formalised, not scheduled, and not certified.",
                3: "Regular, documented short-term or CPD training programs for in-service PHC workers (for example HEWs and health-center staff) are offered, with curricula, a schedule, and certificates of completion. The curricula, schedule, and certification records can be evidenced.",
                4: "All of Level 3, and in addition: an established portfolio of executive and short courses for PHC officers and managers, co-designed with the MoH and/or RHBs; completion is tracked, and the programs are accredited or formally recognized by a relevant national body (where such accreditation exists); and the offering serves PHC workers and officers beyond the institution’s immediate area, with documented uptake and demand.",
              },
            },
          ],
        },
      ],
      narrativeFields: [
        {
          key: "strengths",
          label: "Top 3 Documented Strengths",
          placeholder:
            "Cite indicator code and evidence source for each strength…",
        },
        {
          key: "gaps",
          label: "Top 3 Priority Gaps",
          placeholder:
            "Reference specific CORE indicator codes and describe the gap…",
        },
        {
          key: "departmentalVariation",
          label: "Departmental Variation Flag [V4 NEW]",
          placeholder:
            "Do scores vary significantly across departments? If YES, name the strongest and weakest department…",
        },
        {
          key: "contextualFactors",
          label: "Contextual Factors",
          placeholder:
            "Geography, culture, local government interaction, resource constraints…",
        },
        {
          key: "siteObservations",
          label: "Key Site Observation Notes",
          placeholder: "Direct observations from site visits…",
        },
        {
          key: "smartActions",
          label: "SMART Recommended Actions",
          placeholder:
            "Specific · Measurable · Achievable · Relevant · Time-bound actions…",
        },
      ],
    },
    {
      id: "p2",
      code: "P2",
      name: "Research & Evidence Generation",
      emoji: "🔬",
      weight: 0.3,
      maxRaw: 64,
      maxWeighted: 300,
      weightLabel: "30%",
      color: "#1e4620",
      lightColor: "#f0fdf4",
      domains: [
        {
          id: "d2_1",
          code: "2.1",
          name: "Research Policy, Infrastructure & Ethics",
          maxRaw: 40,
          coreIndicators: ["2.1.1", "2.1.2", "2.1.6"],
          indicators: [
            {
              code: "2.1.1",
              name: "Research Office with PHC Mandate",
              isCore: true,
              herqaStd: "Std 8",
              v4note: null,
              levels: {
                1: "No dedicated research office. Research is managed informally by individual faculty. There is no PHC research mandate and no designated PHC research focal person.",
                2: "A research office or standing research committee exists and functions, but at least one of the following is missing: an explicit PHC research mandate, a designated PHC research focal person, a dedicated budget, or permanent staff. PHC research, where it happens, depends on individual initiative rather than an institutional structure.",
                3: "A functional research office is in place with all four of the following: (a) it operates as a permanent structure with its own staff; (b) an explicit PHC research mandate, with at least one permanent staff member designated as PHC research coordinator; (c) a dedicated, recurrent PHC research budget — allocated annually and ring-fenced for PHC research, not drawn ad hoc from general funds; and (d) a research policy that explicitly prioritizes PHC. The mandate, the budget line, and the policy can each be evidenced in documents.",
                4: "All of Level 3, and in addition: the office is led or supported by senior staff with recognized PHC research expertise and a demonstrable PHC research track record; the dedicated PHC research budget is sustained and multi-year (reliable across budget cycles rather than a single year); and the office plays a leading role beyond the institution — for example convening or leading national or regional PHC research networks, partnerships, or collaborations. PHC research output and funding are growing over time.",
              },
            },
            {
              code: "2.1.2",
              name: "Institutional PHC Research Strategy [HSDIP-I aligned]",
              isCore: true,
              herqaStd: "Std 8",
              v4note: "Revised V4: references HSDIP-I (2025–2030)",
              levels: {
                1: "No institutional research strategy, or a strategy exists but PHC research is not referenced in it or in any related planning document.",
                2: "A research strategy exists and refers to PHC, but PHC is not among its named priority areas or objectives, or the strategy is out of date (not reviewed within the current cycle), or there is no evidence it has been implemented. PHC’s place in the strategy is incidental rather than deliberate.",
                3: "A current research strategy (approved or reviewed within the last three years, and aligned with the national plan in force — currently HSDIP-I 2025–2030) explicitly names PHC research as a priority area, with related priorities such as community health and the Health Extension Program reflected where relevant. Progress against the strategy is reviewed on a regular, documented cycle (at least annually), and there is evidence it is being implemented. The strategy, its alignment, and the review record can each be evidenced in documents.",
                4: "All of Level 3, and in addition: the strategy was co-developed with external stakeholders (for example the Ministry of Health, Regional Health Bureaus, and community representatives); PHC research outputs are tracked against defined milestones or targets; and the strategy is publicly available and has demonstrable influence beyond the institution — for example being referenced by, or feeding into, at least one national or regional policy or plan.",
              },
            },
            {
              code: "2.1.3",
              name: "Ethics Committee Functionality",
              isCore: false,
              herqaStd: "Std 8",
              v4note: null,
              levels: {
                1: "No IRB or ethics committee. Research is conducted without formal ethics review.",
                2: "An ethics committee exists on paper but meets irregularly (fewer than four times a year, not yet quarterly), and the secretariat is not trained.",
                3: "A functional IRB meets at least quarterly (four or more times a year) with a trained secretariat. The standard review form includes a community engagement section, and a community representative sits on the IRB in line with the national SOP. The meeting records, trained-secretariat status, review form, and community representation can be evidenced.",
                4: "All of Level 3, and in addition: the IRB is accredited or formally recognized under the national research-ethics system (where such accreditation exists); the community representative role is formalized and rotating; and the IRB demonstrates wider reach — for example handling multi-site reviews or training ethics reviewers from other HEIs.",
              },
            },
            {
              code: "2.1.4",
              name: "Research Ethics Training",
              isCore: false,
              herqaStd: "Std 8",
              v4note: null,
              levels: {
                1: "No ethics training available. Researchers hold no documented ethics certification.",
                2: "Some faculty have attended external workshops, but there is no institutional program, and postgraduate students receive no formal ethics training.",
                3: "Research ethics training is mandatory for all academic staff and postgraduate students, with completion tracked (and substantially complete at any given time). At least one training event is held annually, and the CITI Programme or an equivalent recognized certification is accepted. The mandatory policy, tracking records, and certification standard can be evidenced.",
                4: "All of Level 3, and in addition: ethics training is embedded in postgraduate orientation and in annual faculty development; and the institution extends its ethics training beyond its own walls — for example offering national ethics training workshops open to other HEIs and NGOs.",
              },
            },
            {
              code: "2.1.5",
              name: "Research Data Management Systems",
              isCore: false,
              herqaStd: "Std 8",
              v4note: null,
              levels: {
                1: "No institutional research data management system. Researchers store data on personal devices or personal cloud accounts. Access is uncontrolled and there is no institutional back-up. No data management policy exists.",
                2: "Research data are held electronically using standard tools (for example Excel, SPSS, Stata, or a data-capture tool such as KoboToolbox or ODK), but on an individual or ad-hoc basis — there is no institutional license or shared data-management environment. Access is not formally controlled (shared files, shared or no passwords), and back-up is informal, irregular, or left to individual researchers. A data management policy is absent or in draft.",
                3: "The institution provides a shared or licensed data-management environment (for example an institutional statistical-software license, a managed shared server, REDCap, or an equivalent electronic data-capture system). Two controls are in place and can be evidenced: access control — data are held in named-user or role-restricted storage rather than open shared folders; and routine back-up — data are backed up on a regular, documented schedule to a separate location. A data management policy exists and IT support is available. The specific product does not determine the level; controlled access and reliable back-up do.",
                4: "An integrated research data management system with tiered, role-based access control and an institutionally hosted electronic data-capture platform (such as REDCap) or equivalent. Back-up is automated, routinely tested for restoration, and geographically or physically separated. Data-sharing and governance protocols comply with WHO and national data-governance standards, and the system supports the full research data lifecycle (capture, storage, sharing, archiving).",
              },
            },
            {
              code: "2.1.6",
              name: "MoH / RHB Implementation Research Collaboration",
              isCore: true,
              herqaStd: "Std 8",
              v4note: null,
              levels: {
                1: "No documented collaboration with the MoH or any RHB on any research activity.",
                2: "Informal relationships with health officials exist, but there are no MoUs, no joint projects, and no shared research agenda.",
                3: "At least one active MoU with an RHB, the MoH, or a health research institute for joint PHC implementation research, with at least one joint study underway or completed (or a joint publication) within the last three years. The MoU and the joint research activity can be evidenced.",
                4: "All of Level 3, and in addition: the collaboration is deep and ongoing — spanning research co-design, joint data collection, and shared dissemination — and the institution holds a substantive role (for example co-investigator) in national or regional PHC research programs. Formal agreements are active across more than one such collaboration where relevant, but depth and genuine joint work, not the number of MoUs, define this level.",
              },
            },
          ],
        },
        {
          id: "d2_2",
          code: "2.2",
          name: "Research Output, Funding & Knowledge Translation",
          maxRaw: 24,
          coreIndicators: ["2.2.1", "2.2.3"],
          indicators: [
            {
              code: "2.2.1",
              name: "PHC Peer-Reviewed Publications",
              isCore: true,
              herqaStd: "Std 8",
              v4note: null,
              levels: {
                1: "Little or no PHC-related peer-reviewed output (of the order of 0–1 publications in three years, relative to the institution’s size and stage). Research output is not an institutional practice.",
                2: "Some PHC-related output (of the order of 2–4 in three years, relative to size and stage), but mostly in local conference proceedings, with few or no indexed-journal publications.",
                3: "A consistent body of peer-reviewed PHC publications in indexed journals over the last three years (of the order of 5 or more for an established institution, or an equivalent output and upward trajectory for a younger or smaller institution relative to its generational benchmark). Authors include both faculty and postgraduate students, and the output includes work in reputable (including international) journals where accessible. The publications and authorship can be evidenced.",
                4: "All of Level 3, and in addition: a strong and growing PHC publication record relative to the institution’s generation (of the order of 10 or more for an established institution, or correspondingly strong for a younger one); and demonstrable research influence — at least one publication has informed a national or regional policy, clinical guideline, or program. Where an institution shows clear policy influence with a somewhat lower publication count, the impact may be weighted as meeting this level.",
              },
            },
            {
              code: "2.2.2",
              name: "External Research Grant Acquisition",
              isCore: false,
              herqaStd: "Std 8",
              v4note: null,
              levels: {
                1: "No external grants secured in the last three years. Research is entirely self-funded or unfunded.",
                2: "One or two small grants secured (relative to size and stage), with no grants from national research councils or international bodies.",
                3: "A working external-funding base over the last three years — of the order of three or more active grants for an established institution, or an equivalent level and upward trajectory for a younger or smaller institution relative to its generational benchmark — including at least one national-level grant (for example EPHI, AHRI, MoSHE) or one international grant for PHC research. The grants and funders can be evidenced.",
                4: "All of Level 3, and in addition: a strong external-funding portfolio relative to the institution’s generation (of the order of five or more active grants for an established institution, or correspondingly strong for a younger one), including multi-year international grants; a mature research-funding operation — for example one that is partly self-sustaining through mechanisms such as indirect cost recovery; and a leadership role in externally-funded multi-institution research (for example as co-PI on regional or national research programs).",
              },
            },
            {
              code: "2.2.3",
              name: "Evidence Dissemination to Health Authorities",
              isCore: true,
              herqaStd: "Std 8",
              v4note: null,
              levels: {
                1: "Research findings are never shared with health authorities. No policy briefs, and no presentations to the MoH or RHBs.",
                2: "Researchers occasionally present at national conferences, but there is no formal dissemination pathway to decision-makers.",
                3: "Systematic dissemination is standard practice for PHC studies: a dissemination plan is prepared as a matter of course, policy briefs are produced for policy-relevant PHC studies (as a guide, around half or more), and at least one annual research-to-policy forum is held with RHB and MoH attendance. The dissemination plans, briefs, and forum can be evidenced.",
                4: "All of Level 3, and in addition: a dedicated knowledge-translation function or team supports dissemination; and major PHC studies routinely produce a policy brief, a lay summary, and a community feedback session, so findings reach both decision-makers and the communities concerned.",
              },
            },
            {
              code: "2.2.4",
              name: "Research Uptake and Policy Impact",
              isCore: false,
              herqaStd: "Std 8",
              v4note: "Renamed V4: outcome-focused rubric",
              levels: {
                1: "No documented evidence that any institutional research finding has influenced a health policy, guideline, or program.",
                2: "Informal accounts of research use exist but are not documented, and there is no formal tracking of research uptake or policy impact.",
                3: "Research uptake is documented and verifiable: at least one or two institutional PHC research findings (relative to the institution’s size and stage) are documented as having informed RHB or MoH decisions, with evidence filed and independently verifiable. A means of tracking uptake exists.",
                4: "All of Level 3, and in addition: the institution maintains a published research uptake register; it has a track record of research demonstrably influencing policy or practice (of the order of several documented cases for an established institution, or correspondingly strong relative to a younger institution’s stage, with weight given to the significance of the impact, not only the number); and the institution is recognized in national or regional PHC policy processes — for example being invited onto PHC guideline-development committees.",
              },
            },
            {
              code: "2.2.5",
              name: "Student PHC Research Involvement",
              isCore: false,
              herqaStd: "Std 8",
              v4note: null,
              levels: {
                1: "No PHC-focused student theses in the last three years. Students select topics independently, with no guidance toward PHC.",
                2: "A few students have chosen PHC topics informally, but there is no institutional encouragement or structured mechanism.",
                3: "A substantial share of postgraduate theses and final-year projects — as a guide, at least 40% over the last three years — address a PHC priority topic, and a PHC research menu or equivalent mechanism guides student topic selection. The proportion and the guiding mechanism can be evidenced.",
                4: "All of Level 3, and in addition: a majority of postgraduate research (as a guide, 60% or more) addresses PHC priorities; student research is increasingly co-authored with faculty and published (see 2.2.1); and strong student research is presented to RHBs or other health-system audiences.",
              },
            },
          ],
        },
      ],
      narrativeFields: [
        {
          key: "strengths",
          label: "Top 3 Documented Research Strengths",
          placeholder: "Cite indicator code and evidence source for each…",
        },
        {
          key: "gaps",
          label: "Top 3 Priority Research Gaps",
          placeholder: "Reference specific CORE indicator codes…",
        },
        {
          key: "knowledgeTranslation",
          label: "Evidence of Knowledge Translation to MoH or RHB",
          placeholder:
            "Cite specific examples, or state that evidence of uptake was absent…",
        },
        {
          key: "contextualFactors",
          label: "Contextual Factors",
          placeholder:
            "Funding landscape, regional research infrastructure, MoH engagement history…",
        },
        {
          key: "smartActions",
          label: "SMART Recommended Actions",
          placeholder:
            "Specific · Measurable · Achievable · Relevant · Time-bound actions…",
        },
      ],
    },
    {
      id: "p3",
      code: "P3",
      name: "Community Service & Engagement",
      emoji: "🤝",
      weight: 0.25,
      maxRaw: 56,
      maxWeighted: 250,
      weightLabel: "25%",
      color: "#744210",
      lightColor: "#fffbeb",
      domains: [
        {
          id: "d3_1",
          code: "3.1",
          name: "CBE, Outreach & Service Delivery",
          maxRaw: 28,
          coreIndicators: ["3.1.1", "3.1.4"],
          indicators: [
            {
              code: "3.1.1",
              name: "CBE Programme Quality",
              isCore: true,
              herqaStd: "Std 9",
              v4note: null,
              levels: {
                1: "No CBE program. Students complete all training at the hospital, with no structured community attachment.",
                2: "CBE exists as a scheduled rotation, but there is no dedicated manual, no structured objectives, and no evaluation system.",
                3: "A documented CBE manual sets out clear objectives, competencies, and evaluation tools. A university supervisor makes at least one documented visit per rotation, and student CBE performance is formally assessed. The manual, visit records, and assessments can be evidenced.",
                4: "All of Level 3, and in addition: the CBE manual is co-developed with communities and RHBs; CBE outcomes are tracked over time (longitudinally, as data accumulate) and reported to the MoH. (A CBE program recognized as a national model represents the top of this level.)",
              },
            },
            {
              code: "3.1.2",
              name: "Health Outreach Programme Coverage",
              isCore: false,
              herqaStd: "Std 9",
              v4note: null,
              levels: {
                1: "No outreach activities organized by the institution, and no community health service beyond the hospital walls.",
                2: "Sporadic outreach events occur, but they are not planned, documented, or tied to PHC priorities, and there is no budget.",
                3: "Regular, documented outreach addressing PHC priorities (as a guide, at least four activities a year, or sustained equivalent programs). Activities are planned, budgeted, and reported, and participation data are recorded. The plans, budgets, reports, and participation data can be evidenced.",
                4: "All of Level 3, and in addition: outreach provides systematic coverage across the institution’s catchment area (the majority of kebeles, on a planned rotating basis), conducted in collaboration with HEWs and kebele health committees.",
              },
            },
            {
              code: "3.1.3",
              name: "Mobile Health / Satellite Clinic Services",
              isCore: false,
              herqaStd: "Std 9",
              v4note: null,
              levels: {
                1: "No mobile health or satellite clinic service, and no context-appropriate alternative for reaching underserved populations.",
                2: "A mobile clinic existed historically but is currently inactive, with no budget and no vehicle or equipment assigned; or ad hoc attempts to reach underserved areas occur without structure.",
                3: "At least one active mobile or satellite service (or an equivalent context-appropriate service) operates in underserved areas on a regular basis (at least monthly). Service records include patient encounters, diagnoses, and referrals, and can be evidenced.",
                4: "All of Level 3, and in addition: the service reaches remote or underserved woredas at scale appropriate to the catchment, is co-funded by the RHB or development partners, and its data feed into the RHB health management information system (HMIS).",
              },
            },
            {
              code: "3.1.4",
              name: "HEW Collaboration in CBE & Outreach",
              isCore: true,
              herqaStd: "Std 9",
              v4note: null,
              levels: {
                1: "No documented engagement with Health Extension Workers in any institutional program.",
                2: "Students occasionally work alongside HEWs during placements, but with no formal arrangement, and HEWs have no role in student orientation or supervision.",
                3: "A formal collaboration agreement covers the health posts used for CBE. HEWs co-supervise students during CBE, joint outreach activities are documented, and HEWs receive formal recognition (for example certificates, acknowledgment, or other agreed recognition). The agreement, co-supervision, joint outreach, and recognition can be evidenced.",
                4: "All of Level 3, and in addition: HEWs are integral co-educators in the CBE program, with documented training, orientation, and evaluation roles; and the institution actively supports HEWs — for example advocating for HEW incentives or contributing to their development.",
              },
            },
            {
              code: "3.1.5",
              name: "Gender & Equity in Outreach",
              isCore: false,
              herqaStd: "Std 9",
              v4note: null,
              levels: {
                1: "No gender or equity considerations in outreach or CBE planning. No sex-disaggregated data collected.",
                2: "Gender or equity is mentioned in outreach plans but not operationalized — there is no specific targeting of marginalized or vulnerable groups, and data are not disaggregated in practice. Inclusion is stated as intent rather than acted on.",
                3: "Outreach plans explicitly identify and target the marginalized and vulnerable groups relevant to the institution’s catchment — for example women, children, the elderly, persons with disabilities, pastoralist or hard-to-reach communities — and sex-disaggregated data are collected and reported. Targeting reflects the actual population served, not a fixed list. Evidence is available in plans and reports.",
                4: "All of Level 3, and in addition: outreach is gender-transformative — it goes beyond including women to actively addressing the barriers and unequal norms that limit participation and benefit (for example timing, location, safety, decision-making, or cultural barriers), rather than only counting female attendance. The institution partners with women’s groups and local gender or social-affairs offices, and outreach impact assessments include a gender and equity analysis that feeds back into planning.",
              },
            },
          ],
        },
        {
          id: "d3_2",
          code: "3.2",
          name: "Partnerships, Social Accountability & Feedback",
          maxRaw: 28,
          coreIndicators: ["3.2.1"],
          indicators: [
            {
              code: "3.2.1",
              name: "RHB & Woreda Health Office Partnership",
              isCore: true,
              herqaStd: "Std 2",
              v4note: null,
              levels: {
                1: "No documented partnership with any Regional Health Bureau, zonal health department, or woreda health office.",
                2: "Relationships exist but are informal — for example, individual faculty know health-office staff personally — with no signed MoU or formal agreement, and no joint planning or reporting. Any collaboration depends on personal contact rather than an institutional arrangement.",
                3: "A formal, functioning partnership is in place: a current, signed MoU or equivalent agreement with at least one relevant health authority (RHB, zonal health department, or woreda health office, as fits the institution’s setting); joint planning or review meetings that actually take place on a regular, documented cycle (at least annually); and documented, shared minutes or records of those meetings. The partnership is active and two-way — not a signed agreement that never convenes.",
                4: "All of Level 3, and in addition: the partnership is multi-level and institutionalized — formal, active agreements span more than one administrative tier (for example regional together with zonal and/or woreda) and are embedded in the institution’s routine operations rather than resting on individuals. The institution holds a formal, ongoing role in joint PHC coordination — for example co-chairing or convening a standing PHC coordination committee, or an equivalent continuing leadership role in regional or sub-regional PHC planning. Partnership activities and their results are tracked over time.",
              },
            },
            {
              code: "3.2.2",
              name: "Community Needs Assessment",
              isCore: false,
              herqaStd: "Std 9",
              v4note: null,
              levels: {
                1: "No community needs assessment conducted. PHC service priorities are set without community input.",
                2: "A community needs assessment was conducted once, longer ago than the regular cycle (several years), and its findings have not been systematically used.",
                3: "A community needs assessment is conducted on a regular cycle (at least every three years) using a standardized tool. Findings are documented and presented to the faculty or research board. The tool, findings, and record of their use can be evidenced.",
                4: "All of Level 3, and in addition: the assessment is participatory and conducted annually, with community members involved as co-investigators; and findings are shared back with the communities concerned in plain language, closing the loop between assessment and the people assessed.",
              },
            },
            {
              code: "3.2.3",
              name: "Community Feedback Mechanism",
              isCore: false,
              herqaStd: "Std 9",
              v4note: null,
              levels: {
                1: "No mechanism for communities to provide feedback to the institution.",
                2: "Feedback can theoretically be given but is never reviewed, and there is no response process.",
                3: "A structured community feedback system is in place, with at least one annual community feedback forum, and feedback is reviewed by a designated committee. The system, the forum, and the review process can be evidenced.",
                4: "All of Level 3, and in addition: feedback is gathered on an ongoing basis through multiple accessible channels (including real-time channels where feasible); how feedback is handled is tracked and reported back; and community feedback explicitly shapes the institution’s annual CBE and outreach plans, so community voice measurably changes what the institution does.",
              },
            },
            {
              code: "3.2.4",
              name: "Dev. Partner, Religious Leader & Community Structure Collaboration",
              isCore: false,
              herqaStd: "Std 9",
              v4note:
                "EXPANDED V4: includes religious leaders, traditional/clan leaders, faith-based organisations",
              levels: {
                1: "No active collaboration with any NGO, development partner, religious institution, traditional leader, or community structure on PHC.",
                2: "Occasional co-activities with NGOs or community groups take place, but with no formal agreement, and religious and traditional leaders are not engaged in any health activities.",
                3: "Active, formal collaboration (MoU or equivalent agreement) with one or more NGOs or development partners for joint PHC activities, together with documented engagement of at least one religious institution or traditional/clan leader in PHC outreach planning. The agreements and the community/faith engagement can be evidenced.",
                4: "All of Level 3, and in addition: a diversified and active partnership portfolio spanning multiple partner types — NGOs and development partners, religious institutions, traditional/clan leaders, and community structures — with genuine, documented joint PHC activity across them; and the institution plays a leading role in collaborative PHC initiatives (for example as a partner or co-investigator in donor-funded or multi-partner PHC projects). Depth and diversity of partnership, not the monetary size of any single project, define this level.",
              },
            },
            {
              code: "3.2.5",
              name: "Budget Allocation for Community Engagement",
              isCore: false,
              herqaStd: "Std 9",
              v4note: null,
              levels: {
                1: "No budget line for community engagement. All activities are informal or dependent on external project funding.",
                2: "Community engagement occurs only when external funding is available. There is no institutional budget line, and activities become unsustainable when external funding ends.",
                3: "A dedicated community engagement budget line exists in the annual institutional budget, funded from the institution’s own recurrent resources and sufficient to sustain the community-engagement program (including CBE logistics, outreach, and partnership coordination). The budget line and its use can be evidenced.",
                4: "All of Level 3, and in addition: the community engagement budget is institutionalised and grows year on year; it is strengthened by additional sources where available (for example revenue from partnerships); and a financial report on community engagement is presented to the Board and made public.",
              },
            },
            {
              code: "3.2.6",
              name: "Community Health Impact Measurement [NEW V4]",
              isCore: false,
              herqaStd: "Std 9",
              v4note:
                "NEW in V4 — response to reviewer comment on process vs outcome",
              levels: {
                1: "No data collected on community health outcomes from engagement activities. Impact is assumed, not measured.",
                2: "Some output data are collected (for example the number of people reached in outreach), but there are no health outcome measurements and no system for tracking community health changes.",
                3: "The institution measures community health outcomes (not only outputs) from its PHC engagement, and documents at least one measured outcome with a reasonable, documented basis for the institution’s contribution to it. Outcome data are shared with the woreda health office. The measurement system and the documented outcome(s) can be evidenced.",
                4: "All of Level 3, and in addition: the institution conducts regular (for example annual, as data accumulate) community health impact assessments using recognised public-health methods that support genuine attribution; and impact findings are published and submitted to RHBs, with demonstrable influence on PHC planning (for example being referenced in regional or national PHC planning processes).",
              },
            },
          ],
        },
      ],
      narrativeFields: [
        {
          key: "strengths",
          label: "Top 3 Community Engagement Strengths",
          placeholder: "Cite indicator code and evidence source for each…",
        },
        {
          key: "gaps",
          label: "Top 3 Priority Gaps",
          placeholder: "Reference specific CORE indicator codes…",
        },
        {
          key: "communityPartnership",
          label: "Evidence of Genuine Community Partnership",
          placeholder:
            "Distinguish from passive data collection. Reference Solomon et al. 2022 if applicable…",
        },
        {
          key: "impactEvidence",
          label: "Community Health Impact Evidence [V4 NEW]",
          placeholder:
            "What evidence exists that engagement activities produced measurable community health outcomes?…",
        },
        {
          key: "contextualFactors",
          label: "Contextual Factors",
          placeholder:
            "Geography, cultural norms, religious/traditional leaders, NGO landscape…",
        },
        {
          key: "smartActions",
          label: "SMART Recommended Actions",
          placeholder:
            "Specific · Measurable · Achievable · Relevant · Time-bound actions…",
        },
      ],
    },
  ],
};

export const ALL_INDICATORS = [];
assessmentData.pillars.forEach((p) =>
  p.domains.forEach((d) =>
    d.indicators.forEach((i) =>
      ALL_INDICATORS.push({
        ...i,
        pillar: p.id,
        pillarName: p.name,
        domain: d.code,
        domainName: d.name,
      }),
    ),
  ),
);

export const CORE_INDICATOR_CODES = new Set(
  ALL_INDICATORS.filter((i) => i.isCore).map((i) => i.code),
);

export function computeScores(scores = {}) {
  const pillarResults = assessmentData.pillars.map((pillar) => {
    let rawTotal = 0;
    pillar.domains.forEach((domain) => {
      domain.indicators.forEach((ind) => {
        const s = scores[ind.code];
        if (s && s >= 1 && s <= 4) rawTotal += ind.isCore ? s * 2 : s;
      });
    });
    const weighted = (rawTotal / pillar.maxRaw) * pillar.maxWeighted;
    return { raw: rawTotal, weighted: Math.round(weighted * 10) / 10 };
  });

  const grandTotal =
    Math.round(pillarResults.reduce((sum, p) => sum + p.weighted, 0) * 10) / 10;

  let gatekeeperPassed = true;
  for (const code of CORE_INDICATOR_CODES) {
    const s = scores[code];
    if (!s || s <= 2) {
      gatekeeperPassed = false;
      break;
    }
  }

  let rating = "Level D";
  if (grandTotal >= 801 && gatekeeperPassed) rating = "Level A";
  else if (grandTotal >= 801) rating = "Level B*";
  else if (grandTotal >= 601) rating = "Level B";
  else if (grandTotal >= 401) rating = "Level C";

  const totalInd = ALL_INDICATORS.length;
  const scoredInd = ALL_INDICATORS.filter(
    (i) => scores[i.code] >= 1 && scores[i.code] <= 4,
  ).length;

  return {
    p1: pillarResults[0],
    p2: pillarResults[1],
    p3: pillarResults[2],
    grandTotal,
    rating,
    gatekeeperPassed,
    completionPct: Math.round((scoredInd / totalInd) * 100),
    scoredCount: scoredInd,
    totalCount: totalInd,
  };
}

export const TARGET_INSTITUTIONS = [
  {
    no: 1,
    name: "Addis Ababa University",
    region: "Addis Ababa",
    generation: "Gen 1",
  },
  { no: 2, name: "Jimma University", region: "Oromia", generation: "Gen 1" },
  {
    no: 3,
    name: "University of Gondar",
    region: "Amhara",
    generation: "Gen 2",
  },
  { no: 4, name: "Hawassa University", region: "SNNPR", generation: "Gen 2" },
  { no: 5, name: "Haramaya University", region: "Oromia", generation: "Gen 2" },
  { no: 6, name: "Mekelle University", region: "Tigray", generation: "Gen 2" },
  {
    no: 7,
    name: "Bahir Dar University",
    region: "Amhara",
    generation: "Gen 2",
  },
  {
    no: 8,
    name: "Arba Minch University",
    region: "SNNPR",
    generation: "Gen 3",
  },
  { no: 9, name: "Wollega University", region: "Oromia", generation: "Gen 3" },
  { no: 10, name: "Dilla University", region: "SNNPR", generation: "Gen 3" },
  {
    no: 11,
    name: "Debre Berhan University",
    region: "Amhara",
    generation: "Gen 3",
  },
  { no: 12, name: "Werabe University", region: "SNNPR", generation: "Gen 4" },
];
